import React, { useEffect, useState } from 'react'
import Breadcrum from '../../components/breadcrum'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getUserActivity } from '../../api/userApi'
import {
  FaDesktop,
  FaMobile,
  FaTabletAlt,
  FaGlobe,
  FaMapMarkerAlt,
  FaWifi,
  FaClock,
} from 'react-icons/fa'
import { handleDate } from '../../handleDateAndPdf'
import Pagination from '../../components/Pagination'

const UserActivity = () => {
  useEffect(() => {
    document.title = 'User Activity Page'
  }, [])

  const [page, setPage] = useState(1)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['userActivity', page],
    queryFn: () => getUserActivity(page),
    onError: (error) => {
      toast.error('Error while fetching data')
    },
  })
  const totalPages = Math.ceil((data?.totalCount || 0) / 10)

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'desktop':
        return <FaDesktop className="w-4 h-4 text-blue-600" />
      case 'mobile':
        return <FaMobile className="w-4 h-4 text-green-600" />
      case 'tablet':
        return <FaTabletAlt className="w-4 h-4 text-purple-600" />
      default:
        return <FaDesktop className="w-4 h-4 text-gray-600" />
    }
  }

  const getBrowserColor = (browser) => {
    switch (browser?.toLowerCase()) {
      case 'chrome':
        return 'bg-blue-500'
      case 'firefox':
        return 'bg-orange-500'
      case 'safari':
        return 'bg-gray-500'
      case 'edge':
        return 'bg-cyan-500'
      default:
        return 'bg-gray-500'
    }
  }

  if (isLoading) {
    return (
      <>
        <Breadcrum subheading="All user" heading="user activity" />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600 font-medium">
            Loading user activity...
          </span>
        </div>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Breadcrum subheading="All user" heading="user activity" />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-red-500 text-xl mb-2">⚠️</div>
            <div className="text-gray-600">
              Error while fetching user activity data
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Breadcrum subheading="All user" heading="user activity" />

      {/* Header Section */}
      <div className="md:w-[95%] mx-auto mb-8">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            User Activity Dashboard
          </h2>
          <p className="text-gray-600">
            Monitor real-time user sessions and activity patterns
          </p>
          <div className="flex items-center gap-4 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-600">
                {data?.totalCount || 0} Active Sessions
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Table */}
      <div className="md:w-[95%] mx-auto">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 my-8">
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Sessions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {data?.totalCount || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FaGlobe className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Online Users</p>
                <p className="text-2xl font-bold text-green-600">
                  {data?.usersData.filter((item) => item.onlineStatus).length ||
                    0}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <div className="w-6 h-6 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Desktop Users</p>
                <p className="text-2xl font-bold text-purple-600">
                  {data?.usersData.filter(
                    (item) => item.deviceType === 'Desktop'
                  ).length || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FaDesktop className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Mobile Users</p>
                <p className="text-2xl font-bold text-orange-600">
                  {data?.usersData.filter(
                    (item) => item.deviceType === 'Mobile'
                  ).length || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <FaMobile className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">
                    Session
                  </th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">
                    Location
                  </th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">
                    Device & Browser
                  </th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">
                    Page Activity
                  </th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">
                    Network
                  </th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">
                    System Info
                  </th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.usersData.map((activity, index) => (
                  <tr
                    key={activity.id}
                    className="hover:bg-gradient-to-r hover:from-blue-25 hover:to-indigo-25 transition-all duration-200"
                  >
                    {/* Session Info */}
                    <td className="py-4 px-4">
                      <div className="space-y-1">
                        <div className="font-medium text-gray-900">
                          #{activity.id}
                        </div>
                        <div className="text-xs text-gray-500">
                          Session: {activity.sessionId?.substring(0, 8)}...
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <FaClock className="w-3 h-3" />
                          Created At:{handleDate(activity.createdAt)}
                        </div>
                      </div>
                    </td>

                    {/* Location */}
                    <td className="py-4 px-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <FaMapMarkerAlt className="w-3 h-3 text-red-500" />
                          <span className="font-medium text-gray-800">
                            {activity.city}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {activity.region}
                        </div>
                        <div className="text-xs text-gray-500">
                          {activity.country} ({activity.countryCode})
                        </div>
                        {activity.latitude && activity.longitude && (
                          <div className="text-xs text-gray-400">
                            {activity.latitude.toFixed(4)},{' '}
                            {activity.longitude.toFixed(4)}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Device & Browser */}
                    <td className="py-4 px-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {getDeviceIcon(activity.deviceType)}
                          <span className="font-medium text-gray-800">
                            {activity.deviceType}
                          </span>
                        </div>
                        <div
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${getBrowserColor(
                            activity.browser
                          )}`}
                        >
                          {activity.browser}
                        </div>
                        <div className="text-xs text-gray-500">
                          {activity.operatingSystem}
                        </div>
                        <div className="text-xs text-gray-400">
                          {activity.screenResolution}
                        </div>
                      </div>
                    </td>

                    {/* Page Activity */}
                    <td className="py-4 px-4">
                      <div className="space-y-1">
                        <div
                          className="font-medium text-gray-900 max-w-48 truncate"
                          title={activity.pageTitle}
                        >
                          📄 {activity.pageTitle}
                        </div>
                        <div
                          className="text-sm text-blue-600 hover:text-blue-800 max-w-48 truncate"
                          title={activity.pageUrl}
                        >
                          <a
                            href={activity.pageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            🔗 {activity.pageUrl}
                          </a>
                        </div>
                        {activity.referrer && (
                          <div
                            className="text-xs text-gray-500 max-w-48 truncate"
                            title={activity.referrer}
                          >
                            From: {activity.referrer}
                          </div>
                        )}
                        <div className="text-xs text-gray-500">
                          Viewport: {activity.viewportSize}
                        </div>
                      </div>
                    </td>

                    {/* Network */}
                    <td className="py-4 px-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <FaWifi className="w-3 h-3 text-green-500" />
                          <span className="font-mono text-sm text-gray-700">
                            {activity.ipAddress}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {activity.isp}
                        </div>
                        <div className="text-xs text-gray-500">
                          Domain: {activity.domain}
                        </div>
                      </div>
                    </td>

                    {/* System Info */}
                    <td className="py-4 px-4">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-700">
                          <FaGlobe className="inline w-3 h-3 mr-1" />
                          {activity.language}
                        </div>
                        <div className="text-xs text-gray-500">
                          Languages: {activity.languages}
                        </div>
                        <div className="text-xs text-gray-500">
                          Timezone: {activity.timezone}
                        </div>
                        <div className="text-xs text-gray-500">
                          Platform: {activity.platform}
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              activity.cookieEnabled
                                ? 'bg-green-500'
                                : 'bg-red-500'
                            }`}
                          ></span>
                          <span className="text-gray-500">
                            Cookies{' '}
                            {activity.cookieEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              activity.touchSupport
                                ? 'bg-blue-500'
                                : 'bg-gray-400'
                            }`}
                          ></span>
                          <span className="text-gray-500">
                            Touch{' '}
                            {activity.touchSupport
                              ? 'Supported'
                              : 'Not Supported'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              activity.consent ? 'bg-green-500' : 'bg-red-500'
                            }`}
                          ></span>
                          <span className="text-gray-500">
                            Consent {activity.consent ? 'Given' : 'Not Given'}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="py-4 px-4">
                      <div className="space-y-2">
                        <div
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            activity.onlineStatus
                              ? 'bg-green-100 text-green-800 border border-green-200'
                              : 'bg-gray-100 text-gray-800 border border-gray-200'
                          }`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full mr-2 ${
                              activity.onlineStatus
                                ? 'bg-green-500 animate-pulse'
                                : 'bg-gray-400'
                            }`}
                          ></div>
                          {activity.onlineStatus ? 'Online' : 'Offline'}
                        </div>
                        {activity.consent && (
                          <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 border border-blue-200">
                            Consent Given
                          </div>
                        )}
                        <div className="text-xs text-gray-500">
                          User time: {activity.localTime}
                        </div>
                        {/* <div className="text-xs text-gray-500">
                          Updated: {handleDate(activity.updatedAt)}
                        </div> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-8 flex justify-center">
          <Pagination page={page} setPage={setPage} totalPages={totalPages} />
        </div>
      </div>
    </>
  )
}

export default UserActivity
